import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Database, Share2, MessageSquare, AlertTriangle,
  BookOpen, LogOut, ChevronRight, Search, Bell, Menu, X, Settings, BarChart3
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ScrollArea';

const navSections = [
  {
    label: 'Main',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', to: '/app/dashboard', active: true },
      { icon: BarChart3, label: 'Admin Dashboard', to: '/app/admin-dashboard', active: true },
    ]
  },
  {
    label: 'Data',
    items: [
      { icon: Database, label: 'Data Sources', to: '/app/data-sources', active: true },
      { icon: Share2, label: 'Semantic Graph', to: '/app/semantic-graph', active: true },
      { icon: BookOpen, label: 'Glossary', to: '/app/glossary', active: true },
    ]
  },
  {
    label: 'AI',
    items: [
      { icon: MessageSquare, label: 'Chat with Data', to: '/app/chat', active: true },
      { icon: AlertTriangle, label: 'Insights', to: '/app/signals', active: true },
    ]
  },
  {
    label: 'Admin',
    items: [
      { icon: Settings, label: 'Saved Queries', to: '/app/saved-queries', active: true },
    ]
  },
];

const pageTitles = {
  '/app/dashboard': { title: 'Dashboard', subtitle: 'Overview of your data intelligence workspace' },
  '/app/admin-dashboard': { title: 'Admin Dashboard', subtitle: 'Workspace administration and monitoring' },
  '/app/data-sources': { title: 'Data Sources', subtitle: 'Manage your database connections' },
  '/app/semantic-graph': { title: 'Semantic Graph', subtitle: 'Explore relationships in your data' },
  '/app/glossary': { title: 'Glossary', subtitle: 'Auto-generated data dictionary' },
  '/app/chat': { title: 'Chat with Data', subtitle: 'Ask questions in natural language' },
  '/app/signals': { title: 'Insights', subtitle: 'Data quality alerts and governance' },
  '/app/saved-queries': { title: 'Saved Queries', subtitle: 'Your bookmarked queries' },
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };
  const currentPage = pageTitles[location.pathname] || { title: 'DQP', subtitle: '' };

  const breadcrumbs = location.pathname.split('/').filter(Boolean).slice(1);

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className={`px-5 py-5 border-b border-[#e6e8f2] flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-[#5b63ea] flex items-center justify-center">
            <span className="text-white font-black text-sm" style={{ fontFamily: 'Outfit' }}>D</span>
          </div>
          {!collapsed && <span className="text-[#1e1e2e] font-bold text-lg tracking-tight" style={{ fontFamily: 'Outfit' }}>DQP</span>}
        </Link>
        <button
          data-testid="sidebar-collapse-btn"
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto text-[#9ca3af] hover:text-[#1e1e2e] transition-colors hidden md:block"
        >
          <Menu size={18} />
        </button>
      </div>

      <ScrollArea className="flex-1 overflow-y-auto py-4 px-3">
        <nav className="space-y-6" aria-label="Workspace navigation">
        {navSections.map((section, si) => (
          <div key={si}>
            {!collapsed && <p className="text-[10px] uppercase tracking-[0.15em] text-[#9ca3af] font-semibold px-3 mb-2">{section.label}</p>}
            <div className="space-y-0.5">
              {section.items.map((item, ii) => {
                const isActive = location.pathname === item.to;
                return item.active ? (
                  <Link
                    key={ii}
                    to={item.to}
                    data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                      isActive
                        ? 'bg-[#5b63ea]/10 text-[#5b63ea] font-medium'
                        : 'text-[#6b7280] hover:bg-[#f5f6fb] hover:text-[#1e1e2e]'
                    } ${collapsed ? 'justify-center' : ''}`}
                  >
                    <item.icon size={18} className="flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                ) : (
                  <div
                    key={ii}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#d4d8e6] cursor-not-allowed ${collapsed ? 'justify-center' : ''}`}
                  >
                    <item.icon size={18} className="flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                    {!collapsed && <span className="ml-auto text-[9px] bg-[#f5f6fb] text-[#9ca3af] px-1.5 py-0.5 rounded uppercase tracking-wider">Soon</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        </nav>
      </ScrollArea>

      <div className={`border-t border-[#e6e8f2] p-4 ${collapsed ? 'flex justify-center' : ''}`}>
        {collapsed ? (
          <div className="w-8 h-8 rounded-full bg-[#5b63ea] flex items-center justify-center text-white text-xs font-bold">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#5b63ea] to-[#8b5cf6] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0 flex-1">
              <p data-testid="sidebar-user-name" className="text-[#1e1e2e] text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[#9ca3af] text-xs truncate">{user?.role === 'admin' ? 'Administrator' : 'Analyst'}</p>
            </div>
            <button data-testid="sidebar-logout-btn" onClick={handleLogout} className="text-[#9ca3af] hover:text-red-500 transition-colors flex-shrink-0">
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="dashboard-layout" className="flex h-screen bg-[#f5f6fb] overflow-hidden">
      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar - desktop */}
      <aside className={`hidden md:flex flex-col bg-white border-r border-[#e6e8f2] flex-shrink-0 transition-all duration-300 ${collapsed ? 'w-[68px]' : 'w-[260px]'}`}>
        {sidebarContent}
      </aside>

      {/* Sidebar - mobile */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-[260px] bg-white border-r border-[#e6e8f2] transform transition-transform md:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {sidebarContent}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Bar */}
        <header data-testid="dashboard-topbar" className="h-14 bg-white border-b border-[#e6e8f2] flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            <button className="md:hidden text-[#6b7280]" onClick={() => setMobileOpen(true)} data-testid="mobile-sidebar-btn">
              <Menu size={20} />
            </button>
            <div className="hidden sm:flex items-center gap-1.5 text-sm">
              <span className="text-[#9ca3af]">App</span>
              {breadcrumbs.map((crumb, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  <ChevronRight size={12} className="text-[#d4d8e6]" />
                  <span className={i === breadcrumbs.length - 1 ? 'text-[#1e1e2e] font-medium' : 'text-[#9ca3af]'}>
                    {crumb.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 bg-[#f5f6fb] rounded-lg px-3 py-1.5 border border-[#e6e8f2] w-52">
              <Search size={14} className="text-[#9ca3af]" />
              <span className="text-[#9ca3af] text-xs">Search...</span>
              <kbd className="ml-auto text-[10px] bg-white border border-[#e6e8f2] rounded px-1 py-0.5 text-[#9ca3af]">&#8984;K</kbd>
            </div>
            <button data-testid="notifications-btn" className="relative text-[#9ca3af] hover:text-[#1e1e2e] transition-colors">
              <Bell size={18} />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#5b63ea] rounded-full" />
            </button>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#5b63ea] to-[#8b5cf6] flex items-center justify-center text-white text-xs font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          </div>
        </header>

        {/* Content */}
        <ScrollArea as="main" className="flex-1 overflow-y-auto">{children}</ScrollArea>
      </div>
    </div>
  );
}
